/**
 * Agent-first New and Edit (designer's notes, 2026-10-25; Prototype afVals()):
 * creating content, and editing an article or a campaign, opens a two-item
 * menu — "Ask your agent" (Recommended) first, then the person's own way.
 * This is the one place for each action's wording, the prompt its "Ask your
 * agent" modal starts with, and its do-it-myself item. The do-it-myself action
 * itself runs on the screen (AgentFirstButton's onSelf), since it opens that
 * screen's form.
 *
 * No `self` = no form to fill in, so the button opens the modal directly with
 * no menu (New form). Prompts say lowercase "tinycms" and "send it to me to
 * approve" wherever the work waits for an approval.
 */

export type AgentFirstKey =
  | 'article'
  | 'page'
  | 'author'
  | 'form'
  | 'formPlace'
  | 'articleEdit'
  | 'articlePosts'
  | 'post'
  | 'rule'
  | 'campaign'
  | 'campaignEdit'
  | 'healthFix';

/** What a prompt can name: the article or campaign being edited or posted about. */
export interface AgentFirstContext {
  title?: string | null;
  /** healthFix: what's held on the item, as the agent should fix it. */
  fix?: string | null;
}

export interface AgentFirstAction {
  /** Under "Ask your agent": what the agent will do. */
  agentSub: string;
  /** The person's own way: its menu item, and the line on what the form covers. */
  self?: { label: string; sub: string };
  prompt: (ctx: AgentFirstContext) => string;
}

const quoted = (title: string | null | undefined, fallback: string) =>
  title?.trim() ? `“${title.trim()}”` : fallback;

export const AGENT_FIRST: Record<AgentFirstKey, AgentFirstAction> = {
  article: {
    agentSub: 'It drafts it from your sources and cites them, for you to approve',
    self: { label: 'Write it myself', sub: 'Open a blank article in the editor' },
    prompt: () =>
      'Draft an article in tinycms about [topic]. Use what we’ve published and our sources, cite them, add a summary and tags, and send it to me to approve.',
  },
  page: {
    agentSub: 'It builds the page from your content, for you to publish',
    self: { label: 'Build it myself', sub: 'Title, address and a starting layout' },
    prompt: () =>
      'Build a page in tinycms called [title] for [what it’s for], using my site’s template and what we’ve published. Send it to me to approve.',
  },
  author: {
    agentSub: 'It adds their bio, photo and links, for you to check',
    self: { label: 'Add them myself', sub: 'Name, role, bio, photo and links' },
    // Authors are saved as they're made (no approval), so the prompt doesn't promise one.
    prompt: () =>
      'Create an author in tinycms for [name], [role]. Add a two-sentence bio, a photo, and their X and LinkedIn handles. Then credit their articles to them in the byline.',
  },
  form: {
    agentSub: 'It builds the form and places it on a page, for you to publish',
    prompt: () =>
      'Build a form in tinycms that asks for [what to collect] and place it on [page]. Send it to me to approve.',
  },
  // A form that isn't on any page (one a template added): placing it is the agent's.
  formPlace: {
    agentSub: 'It puts the form on a page, for you to publish',
    prompt: ({ title }) =>
      `Put the tinycms form ${quoted(title, '[form]')} on [page], where readers will see it. Send the page to me to approve.`,
  },
  articleEdit: {
    agentSub: 'Say what to change; it edits and sends it back to approve',
    self: { label: 'Edit it myself', sub: 'Open this article in the editor' },
    prompt: ({ title }) =>
      `Edit the tinycms article ${quoted(title, '[title]')}: [what to change]. Keep the facts and citations, and send the changes to me to approve.`,
  },
  articlePosts: {
    agentSub: 'It writes a post for each connected channel, for you to approve',
    self: { label: 'Write it myself', sub: 'Opens the post with this article linked' },
    prompt: ({ title }) =>
      `Write social posts in tinycms about ${quoted(title, '[article]')} for each connected channel. Link the article, keep each post within the channel’s limit, and send them to me to approve.`,
  },
  post: {
    agentSub: 'It writes the post for each channel, for you to approve',
    self: { label: 'Write it myself', sub: 'Text, link, image, channels and time' },
    prompt: () =>
      'Write a social post in tinycms about [article or topic] for [channels]. Keep each one within the channel’s limit, link the article, and send it to me to approve.',
  },
  rule: {
    agentSub: 'It sets up the rule; you turn it on',
    self: { label: 'Set it up myself', sub: 'Topics, channels, schedule and approval' },
    prompt: () =>
      'Set up a sharing rule in tinycms that posts new [topic] articles to [channels], [days and times]. Ask me to approve each post.',
  },
  campaign: {
    agentSub: 'It drafts the plan, page and posts for you to approve',
    self: { label: 'Set it up myself', sub: 'Name, brief, dates, channels and landing page' },
    prompt: () =>
      'Plan a campaign in tinycms called [name] to [goal], running [dates]. Draft the landing page, the posts for each channel and the tasks, and send them to me to approve.',
  },
  healthFix: {
    agentSub: 'It fixes what’s held and sends it back for you to approve',
    prompt: ({ title, fix }) =>
      `${fix?.trim() || `Fix what’s held on ${quoted(title, 'this item')} in tinycms.`} Send the change to me to approve.`,
  },
  campaignEdit: {
    agentSub: 'Say what to change; it updates it and sends it back to approve',
    self: { label: 'Edit it myself', sub: 'Open the settings' },
    prompt: ({ title }) =>
      `Update the tinycms campaign ${quoted(title, '[campaign]')}: [what to change]. Send the changes to me to approve.`,
  },
};

export const agentPrompt = (key: AgentFirstKey, ctx: AgentFirstContext = {}) =>
  AGENT_FIRST[key].prompt(ctx);

/** The modal's words: who to ask, and how to say what was copied. */
export function askWords(agentName: string | null | undefined) {
  const name = agentName?.trim() || '';
  const who = name || 'your agent';
  return {
    title: name ? `Ask ${name}` : 'Ask your agent',
    sub: `Copy this prompt into ${who}. It does the work in tinycms and sends it to you. Nothing goes live until you approve it.`,
    copied: `Prompt copied. Paste it into ${who}.`,
  };
}
